import React, { useState, useEffect, useMemo } from "react";
import config from "../../Config";
import axios from "axios";
import Spinner from "react-bootstrap/Spinner";
import PrintHeader from "../General/PrintHeader";
import Page from "../General/Page";
import { calculateColumnTotal, getMonthName } from "../General/Utility";

const ArrearsRecoveryReportPrint = () => {
  const [AccessKey] = useState(localStorage.getItem("AccessKey"));
  const [UserID] = useState(localStorage.getItem("ID"));
  const [MemberType] = useState(localStorage.getItem("MemberType"));
  const [loading, setLoading] = useState(false);
  const [listOfLedger, setLedger] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [totalArrearsRecovered, setTotalArrearsRecovered] = useState(0);
  const [totalRecoverableFee, setTotalRecoverableFee] = useState(0);
  const [grandTotalArrears, setGrandTotalArrears] = useState(0);
  const [totalPendingArrears, setTotalPendingArrears] = useState(0);
  const [includePendingArrears, setIncludePendingArrears] = useState(false);

  const titleFromUrl = useMemo(() => {
    const q = new URLSearchParams(
      typeof window !== "undefined" ? window.location.search : ""
    );
    const y = q.get("ReportYear") || "";
    const m = q.get("ReportMonth") || "";
    const mi = parseInt(String(m).trim(), 10);
    const monthLabel =
      Number.isFinite(mi) && mi >= 1 && mi <= 12 ? getMonthName(mi) : m || "—";
    const pending = q.get("IncludePendingArrears") === "1";
    return `Arrears recovery report for ${monthLabel} ${y}${
      pending ? " (incl. pending)" : ""
    }`.trim();
  }, []);

  useEffect(() => {
    document.title = "Arrears Recovery Print";
    setLoading(true);
    const urlParams = new URLSearchParams(window.location.search);

    const reportYearRaw = urlParams.get("ReportYear");
    const reportMonthRaw = urlParams.get("ReportMonth");
    const sessionRaw =
      urlParams.get("Session_Id") ?? urlParams.get("SessionID") ?? "";
    const wingRaw = urlParams.get("Wing_Id") ?? "";
    const classRaw = urlParams.get("Class_Id") ?? "";
    const sectionRaw = urlParams.get("Section_Id") ?? "";
    const orderByRaw = urlParams.get("OrderBy") ?? "";
    const includePending =
      urlParams.get("IncludePendingArrears") === "1" ||
      urlParams.get("IncludePendingArrears") === "true";

    setIncludePendingArrears(includePending);

    const ry = parseInt(String(reportYearRaw ?? "").trim(), 10);
    const rm = parseInt(String(reportMonthRaw ?? "").trim(), 10);
    const sess = parseInt(String(sessionRaw).trim(), 10);
    const w = parseInt(String(wingRaw).trim(), 10);
    const c = parseInt(String(classRaw).trim(), 10);
    const s = parseInt(String(sectionRaw).trim(), 10);

    axios({
      method: "post",
      url: config.base_url + "Reports/GetArrearsRecoveryReport",
      headers: { "Content-Type": "application/json" },
      data: {
        AccessKey,
        UserID,
        MemberType,
        pageNo: 1,
        pageSize: 10000,
        ReportYear: Number.isFinite(ry) ? ry : 0,
        ReportMonth: Number.isFinite(rm) ? rm : 0,
        SessionID: Number.isFinite(sess) && sess > 0 ? sess : 0,
        Wing_Id: Number.isFinite(w) ? w : 0,
        Class_Id: Number.isFinite(c) ? c : 0,
        Section_Id: Number.isFinite(s) ? s : 0,
        OrderBy: orderByRaw || "Wing_Name,Class_Name,Section_Name",
        IncludePendingArrears: includePending,
      },
    })
      .then(function (response) {
        const rows = response.data.ListofRecords || [];
        setLedger(rows);
        setTotalRecords(response.data.totalRecords ?? rows.length);
        setTotalArrearsRecovered(response.data.TotalArrearsRecovered || 0);
        setTotalRecoverableFee(response.data.TotalRecoverableFee || 0);
        setGrandTotalArrears(response.data.GrandTotalArrears || 0);
        setTotalPendingArrears(response.data.TotalPendingArrears || 0);
        setLoading(false);
      })
      .catch(function () {
        setLedger([]);
        setTotalRecords(0);
        setTotalArrearsRecovered(0);
        setTotalRecoverableFee(0);
        setGrandTotalArrears(0);
        setTotalPendingArrears(0);
        setLoading(false);
      });
  }, [AccessKey, UserID, MemberType]);

  const colSpan = includePendingArrears ? 10 : 9;

  const renderLedger = () => {
    if (listOfLedger != null && listOfLedger.length > 0) {
      let count = 1;
      return listOfLedger.map((item, index) => (
        <tr key={index}>
          <td>{count++}</td>
          <td>{item.Student_ID}</td>
          <td>{item.Registration_No}</td>
          <td>{item.Student_Name}</td>
          <td>{item.Father_Name}</td>
          <td>{item.Class_Name}</td>
          <td>{item.Section_Name}</td>
          <td className="text-end">{item.ArrearsRecovered}</td>
          {includePendingArrears && (
            <td className="text-end">{item.RecoverableFee}</td>
          )}
          <td className="text-end">{item.TotalArrears}</td>
        </tr>
      ));
    }
    return (
      <tr>
        <td colSpan={colSpan}>
          <h4 className="mt-3" style={{ fontSize: "15px", color: "rgba(0,0,0,0.6)" }}>
            No Data Found
          </h4>
        </td>
      </tr>
    );
  };

  return (
    <Page orientation="landscape" size="legal">
      <table className="table table-sm table-bordered table-print">
        <thead>
          <tr>
            <th colSpan={colSpan}>
              <PrintHeader title={titleFromUrl} />
            </th>
          </tr>
        </thead>
        <tbody>
          {loading ? (
            <tr>
              <td className="text-center" colSpan={colSpan}>
                <Spinner animation="border" role="status" />
              </td>
            </tr>
          ) : (
            <>
              <tr>
                <th>Sr.#</th>
                <th>Student ID</th>
                <th>Reg. No</th>
                <th>Student Name</th>
                <th>Father Name</th>
                <th>Class</th>
                <th>Section</th>
                <th className="text-end">Arrears Recovered</th>
                {includePendingArrears && (
                  <th className="text-end">Pending Arrears</th>
                )}
                <th className="text-end">Total Arrears</th>
              </tr>
              {renderLedger()}
              <tr>
                <th colSpan={6} className="text-end">
                  Total ({totalRecords} students) — Recovered: {totalArrearsRecovered}
                  {includePendingArrears && <> | Pending: {totalPendingArrears}</>}
                </th>
                <th className="text-end">
                  {totalArrearsRecovered ||
                    calculateColumnTotal(listOfLedger, "ArrearsRecovered")}
                </th>
                {includePendingArrears && (
                  <th className="text-end">
                    {totalRecoverableFee ||
                      calculateColumnTotal(listOfLedger, "RecoverableFee")}
                  </th>
                )}
                <th className="text-end">
                  {grandTotalArrears ||
                    calculateColumnTotal(listOfLedger, "TotalArrears")}
                </th>
              </tr>
            </>
          )}
        </tbody>
      </table>
    </Page>
  );
};

export default ArrearsRecoveryReportPrint;
